/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISitePickerViewProps } from '@msdyn365-commerce-modules/site-picker';

import { ISiteOptionsData, ISitePickerConfig } from '../../master/definition-extensions/site-picker.ext.props.autogenerated';

interface IExtendedSitePickerViewProps extends Omit<ISitePickerViewProps, 'config'> {
    store: IModuleProps;
    config: ISitePickerConfig;
}

export default (props: IExtendedSitePickerViewProps) => {
    return <>{_renderSitePicker(props)}</>;
};

const _renderDesktopView = (
    props: IExtendedSitePickerViewProps,
    currentLanguage?: ISiteOptionsData,
    showShortSiteNameLargeScreen?: boolean
) => {
    const { SitePickerMenu, SitePickerMenuSpan } = props;

    return (
        <>
            <Node {...SitePickerMenu} onClick={props.renderSiteOptions}>
                {showShortSiteNameLargeScreen ? currentLanguage?.shortSiteName : currentLanguage?.siteName}
                <Node {...SitePickerMenuSpan} />
            </Node>
            {props.siteOptionsState ? _renderSiteOptionsList(props, currentLanguage) : null}
        </>
    );
};

const _renderSiteOptionsList = (props: IExtendedSitePickerViewProps, currentLanguage?: ISiteOptionsData): React.ReactNode => {
    const { SitePickerMenuList, SitePickerMenuListItem, SitePickerMenuListItemArray } = props;

    const options = SitePickerMenuListItemArray!
        .filter(
            item =>
                !(
                    React.isValidElement(item) &&
                    item.props.href.toLowerCase() === currentLanguage?.siteRedirectUrl.destinationUrl.toLowerCase()
                )
        )
        .map((item, index) => (
            <Node {...SitePickerMenuListItem} key={index}>
                {item}
            </Node>
        ));

    return (
        <Node {...SitePickerMenuList} onMouseLeave={props.resetSiteOptionsState}>
            {options}
        </Node>
    );
};

const _renderMobileView = (props: IExtendedSitePickerViewProps, currentLanguage?: ISiteOptionsData): React.ReactNode => {
    const { SitePickerMobileContainer } = props;
    SitePickerMobileContainer.toggleButtonText = currentLanguage?.shortSiteName;

    return (
        <Node className={`ms-site-picker__container`}>
            <Node {...SitePickerMobileContainer} onClick={props.renderSiteOptions}>
                <div>
                    {props.SitePickerMenuListItemArray?.filter(
                        item =>
                            !(
                                React.isValidElement(item) &&
                                item.props.href.toLowerCase() === currentLanguage?.siteRedirectUrl.destinationUrl.toLocaleLowerCase()
                            )
                    ).map((item, index) => {
                        return <div key={index}>{item}</div>;
                    })}
                </div>
            </Node>
        </Node>
    );
};

const _renderSitePicker = (props: IExtendedSitePickerViewProps): React.ReactNode => {
    // Extends the original site picker behavior
    // Checks for current site path value and evaluates set language
    const { sitePath } = props.store.requestContext;
    const { siteOptions, showShortSiteNameLargeScreen } = props.config;

    let defaultLanguage: ISiteOptionsData = {
        siteRedirectUrl: { destinationUrl: sitePath },
        shortSiteName: props.config.heading?.text ?? ''
    };

    let currentLanguage: ISiteOptionsData = {
        ...(siteOptions?.find(
            (option): option is ISiteOptionsData => option.siteRedirectUrl.destinationUrl.toLowerCase() === sitePath.toLowerCase()
        ) ?? defaultLanguage)
    };

    const { isMobileView, SitePicker } = props;
    return (
        <Module {...SitePicker}>
            {isMobileView
                ? _renderMobileView(props, currentLanguage)
                : _renderDesktopView(props, currentLanguage, showShortSiteNameLargeScreen)}
        </Module>
    );
};
